import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import clsx from 'clsx'

import { PrimaryButton } from './Button'
import { getStartedLink } from '@/config'

export interface TOCHeading {
  id: string;
  top: number;
}

export interface TOCSection {
  id: string;
  title: string;
  children: TOCSection[];
}

function useTableOfContents(tableOfContents: TOCSection[]): string | undefined {
  const [currentSection, setCurrentSection] = useState<string | undefined>(tableOfContents[0]?.id)

  const getHeadings = useCallback((tableOfContents: TOCSection[]) => {
    return tableOfContents
      .flatMap((node) => [node.id, ...node.children.map((child) => child.id)])
      .map((id) => {
        const el = document.getElementById(id)
        if (!el) return undefined

        const style = window.getComputedStyle(el)
        const scrollMt = parseFloat(style.scrollMarginTop)

        const top = window.scrollY + el.getBoundingClientRect().top - scrollMt
        return { id, top }
      })
      .filter((heading): heading is TOCHeading => heading !== undefined)
  }, [])

  useEffect(() => {
    if (tableOfContents.length === 0) return
    const headings = getHeadings(tableOfContents)
    if (headings.length === 0) return

    function onScroll() {
      const top = window.scrollY
      let current = headings[0].id
      for (const heading of headings) {
        if (top >= heading.top - 1) {
          // no idea why this is needed but it is
          current = heading.id
        } else {
          break
        }
      }
      setCurrentSection(current)
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    onScroll()
    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [getHeadings, tableOfContents])

  return currentSection
}

interface TableOfContentsProps {
  tableOfContents: TOCSection[];
  title?: string;
  width?: string;
  showCTA?: boolean;
}

export function TableOfContents({
  tableOfContents,
  title = 'Contents',
  width = 'w-72',
  showCTA = false,
}: TableOfContentsProps) {
  const currentSection = useTableOfContents(tableOfContents)

  function isActive(section: TOCSection): boolean {
    if (section.id === currentSection) {
      return true
    }
    if (!section.children) {
      return false
    }
    return section.children.findIndex(isActive) > -1
  }

  return (
    <div
      className={clsx(
        'mb-12 hidden space-y-5 xl:sticky xl:top-12 xl:-mr-6 xl:block xl:h-fit xl:flex-none xl:overflow-y-auto xl:pr-6 xl:pt-16',
        width
      )}
    >
      <nav aria-labelledby="on-this-page-title">
        {tableOfContents.length > 0 && (
          <>
            <h2
              id="on-this-page-title"
              className="font-display flex items-center gap-x-2 text-sm font-medium text-slate-400"
            >
              <svg
                className="fill-slate-400"
                width="16"
                height="16"
                viewBox="0 0 16 16"
                role="img"
                focusable="false"
                aria-hidden="true"
                style={{ '--icon-color': 'var(--color-text-tertiary)' } as React.CSSProperties}
              >
                <path d="M2 3.5a.75.75 0 0 1 .75-.75h7.5a.75.75 0 0 1 0 1.5h-7.5A.75.75 0 0 1 2 3.5ZM2 8a.75.75 0 0 1 .75-.75h10.5a.75.75 0 0 1 0 1.5H2.75A.75.75 0 0 1 2 8Zm.75 3.75a.75.75 0 0 0 0 1.5h5.5a.75.75 0 0 0 0-1.5h-5.5Z"></path>
              </svg>
              {title}
            </h2>
            <ol role="list" className="ml-1 mt-4 text-sm">
              {tableOfContents.map((section) => (
                <li
                  key={section.id}
                  className={clsx(
                    isActive(section)
                      ? 'border-l-2 border-sky-500'
                      : 'border-l-2 border-slate-800',
                    'py-2'
                  )}
                >
                  <div className="pl-3">
                    <h3>
                      <Link
                        href={`#${section.id}`}
                        className={clsx(
                          isActive(section)
                            ? ' text-sky-500'
                            : 'font-normal text-slate-400 hover:text-slate-300'
                        )}
                      >
                        {section.title}
                      </Link>
                    </h3>
                    {section.children.length > 0 && (
                      <ol
                        role="list"
                        className="mt-2 space-y-2 pl-4 text-slate-400"
                      >
                        {section.children.map((subSection) => (
                          <li
                            key={subSection.id}
                            className={clsx(
                              section.id == 'frequently-asked-questions'
                                ? 'hidden'
                                : '',
                              isActive(subSection)
                                ? 'text-sky-500'
                                : 'hover:text-slate-300'
                            )}
                          >
                            <Link href={`#${subSection.id}`}>
                              {subSection.title}
                            </Link>
                          </li>
                        ))}
                      </ol>
                    )}
                  </div>
                </li>
              ))}
            </ol>
          </>
        )}
      </nav>
      {showCTA && (
        <div className="flex flex-col items-center rounded-lg bg-slate-800/60 p-4 pb-6">
          <h3 className="font-display text-center text-xl font-medium text-sky-400">
            Optimize your Snowflake usage
          </h3>

          <p className="mb-4 mt-2 break-normal text-center text-sm text-slate-300 ">
            SELECT automatically optimizes and helps you manage your Snowflake
            usage with ease.
          </p>
          <PrimaryButton textSize="text-sm" href={getStartedLink}>
            Try for free
          </PrimaryButton>
        </div>
      )}
    </div>
  )
}